import { useMutation } from '@apollo/client';
import * as Dialog from '@radix-ui/react-dialog';
import { Plus, Upload, X } from 'lucide-react';
import * as React from 'react';
import { useRef, useState } from 'react';
import { CIRCLES } from '../../constants/circles.constants';
import { CREATE_CIRCLE } from '../../graphql/resolvers/circleMutations/createCircle.gql';
import { parseCircleCsv } from '../../helpers/parseCircleCsv';
import { parseCircleMarkdown } from '../../helpers/parseCircleMarkdown';
import ButtonComponent from '../UI/Button/Button.component';

interface CreateCircleModalProps {
  open: boolean;
  onClose: () => void;
}

const INITIAL_INPUT_COUNT = 5;
const MAX_MANUAL_INPUTS = 20;

const CreateCircleModal = ({ open, onClose }: CreateCircleModalProps) => {
  const mdFileInputRef = useRef<HTMLInputElement>(null);
  const csvFileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [memberInputs, setMemberInputs] = useState<string[]>(
    Array(INITIAL_INPUT_COUNT).fill(''),
  );
  const [fileMembers, setFileMembers] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [createCircle, { loading }] = useMutation(CREATE_CIRCLE, {
    update(cache, { data }) {
      const created = data?.createCircle;
      if (!created) return;
      cache.modify({
        fields: {
          circles(existing = [], { toReference }) {
            const ref = toReference(created);
            return ref ? [ref, ...existing] : existing;
          },
        },
      });
    },
  });

  const resetForm = () => {
    setName('');
    setMemberInputs(Array(INITIAL_INPUT_COUNT).fill(''));
    setFileMembers(null);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleMemberChange = (index: number, value: string) => {
    setMemberInputs((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleRemoveMember = (index: number) => {
    if (memberInputs.length <= 1) return;
    setMemberInputs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddMemberInput = () => {
    if (memberInputs.length >= MAX_MANUAL_INPUTS) return;
    setMemberInputs((prev) => [...prev, '']);
  };

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    parser: (raw: string) => string[],
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parser(text);
      if (parsed.length === 0) {
        setError(CIRCLES.ERRORS.NO_VALID_USERNAMES);
        return;
      }
      setFileMembers(parsed);
      setError(null);
    };
    reader.readAsText(file);

    event.target.value = '';
  };

  const handleClearFile = () => {
    setFileMembers(null);
  };

  const getMembers = (): string[] => {
    if (fileMembers) return fileMembers;
    return memberInputs
      .map((m) => m.trim().replace(/^@/, ''))
      .filter((m) => m.length > 0);
  };

  const handleCreate = async () => {
    setError(null);
    const trimmedName = name.trim();

    if (!trimmedName) {
      setError(CIRCLES.ERRORS.NAME_REQUIRED);
      return;
    }
    if (trimmedName.length > 90) {
      setError(CIRCLES.ERRORS.NAME_MAX_LENGTH);
      return;
    }

    const members = getMembers();
    if (members.length === 0) {
      setError(CIRCLES.ERRORS.MEMBER_REQUIRED);
      return;
    }

    try {
      const { data } = await createCircle({
        variables: { input: { name: trimmedName, members } },
      });

      if (data?.createCircle) {
        handleClose();
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : CIRCLES.ERRORS.FAILED_CREATE,
      );
    }
  };

  const useTwoColumns = memberInputs.length >= 10;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => !isOpen && handleClose()}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="create-circle-modal__overlay" />
        <Dialog.Content className="create-circle-modal__content">
          <Dialog.Title className="create-circle-modal__title">
            {CIRCLES.MODAL.CREATE_TITLE}
          </Dialog.Title>

          <div className="create-circle-modal__field">
            <label htmlFor="circle-name">{CIRCLES.MODAL.NAME_LABEL}</label>
            <input
              id="circle-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={CIRCLES.MODAL.NAME_PLACEHOLDER}
              maxLength={90}
            />
          </div>

          <p className="create-circle-modal__members-label">
            {CIRCLES.MODAL.MEMBERS_LABEL}
          </p>

          {fileMembers ? (
            <>
              <div className="create-circle-modal__file-chips">
                {fileMembers.map((username) => (
                  <span key={username} className="chip">
                    {username}
                  </span>
                ))}
              </div>
              <p className="create-circle-modal__file-badge">
                {fileMembers.length} {CIRCLES.MODAL.FROM_FILE}
              </p>
              <button
                type="button"
                className="create-circle-modal__add-member-btn"
                onClick={handleClearFile}
              >
                {CIRCLES.MODAL.CLEAR_FILE}
              </button>
            </>
          ) : (
            <>
              <div
                className={`create-circle-modal__members-list ${useTwoColumns ? 'create-circle-modal__members-list--two-col' : ''}`}
              >
                {memberInputs.map((value, index) => (
                  <div key={index} className="create-circle-modal__member-row">
                    <input
                      type="text"
                      value={value}
                      onChange={(e) =>
                        handleMemberChange(index, e.target.value)
                      }
                      placeholder={CIRCLES.MODAL.USERNAME_PLACEHOLDER}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(index)}
                      aria-label={CIRCLES.ARIA.REMOVE_MEMBER}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="create-circle-modal__add-member-btn"
                onClick={handleAddMemberInput}
                disabled={memberInputs.length >= MAX_MANUAL_INPUTS}
              >
                <Plus size={14} />
                {CIRCLES.MODAL.ADD_MEMBER}
                {memberInputs.length >= MAX_MANUAL_INPUTS &&
                  CIRCLES.MODAL.ADD_MEMBER_MAX}
              </button>
            </>
          )}

          <div className="create-circle-modal__file-upload">
            <div className="create-circle-modal__file-row">
              <button
                type="button"
                className="create-circle-modal__file-btn"
                onClick={() => mdFileInputRef.current?.click()}
              >
                <Upload size={14} />
                {CIRCLES.MODAL.UPLOAD_MD}
              </button>
              <span className="create-circle-modal__file-hint">
                {CIRCLES.MODAL.HINT_MD}
              </span>
            </div>
            <div className="create-circle-modal__file-row">
              <button
                type="button"
                className="create-circle-modal__file-btn"
                onClick={() => csvFileInputRef.current?.click()}
              >
                <Upload size={14} />
                {CIRCLES.MODAL.UPLOAD_CSV}
              </button>
              <span className="create-circle-modal__file-hint">
                {CIRCLES.MODAL.HINT_CSV}
              </span>
            </div>
            <input
              ref={mdFileInputRef}
              type="file"
              accept=".md"
              onChange={(e) => handleFileUpload(e, parseCircleMarkdown)}
              className="visually-hidden"
            />
            <input
              ref={csvFileInputRef}
              type="file"
              accept=".csv"
              onChange={(e) => handleFileUpload(e, parseCircleCsv)}
              className="visually-hidden"
            />
          </div>

          {error && <p className="create-circle-modal__error">{error}</p>}

          <div className="create-circle-modal__footer">
            <ButtonComponent variant="secondary" onClick={handleClose}>
              {CIRCLES.BUTTONS.CANCEL}
            </ButtonComponent>
            <ButtonComponent
              variant="primary"
              onClick={handleCreate}
              loading={loading}
              disabled={loading}
            >
              {CIRCLES.BUTTONS.CREATE}
            </ButtonComponent>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default CreateCircleModal;
